import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGetCitiesQuery, useGetRegionsQuery } from "@/shared/api/common";
import type { ContractFormValues } from "@/features/hr/contracts/types";
import { RegionCitySelect, DatePicker, Input } from "@/shared/ui";
import { formatDateToISO, type Locale } from "@/shared/utils";

interface Props {
  contractNumberError?: string;
  contractNumberEditable?: boolean;
}

export default function BasicInfoForm({ contractNumberError, contractNumberEditable = true }: Props) {
  const { i18n, t } = useTranslation("FillContractPage");
  const locale = (i18n.language as Locale) || "ru";
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ContractFormValues>();

  const { data: cities = [] } = useGetCitiesQuery();
  const { data: regions = [] } = useGetRegionsQuery();
  const [regionId, setRegionId] = useState<number | undefined>(undefined);

  const workCityId = watch("work_city_id");

  useEffect(() => {
    if (regionId !== undefined || !workCityId || (regions.length === 0 && cities.length === 0)) return;

    const nationalRegion = regions.find((r) => r.id === workCityId && r.city_type === "national_city");
    if (nationalRegion) {
      setRegionId(workCityId);
      return;
    }

    const city = cities.find((c) => c.id === workCityId);
    if (city?.parent_region) {
      setRegionId(city.parent_region);
    }
  }, [workCityId, regions, cities, regionId]);

  const signDate = watch("sign_date");
  const startDate = watch("start_date");

  function handleDateChange(v: Date | { start: Date | null; end: Date | null } | null) {
    const date = v && "start" in v ? v.start : (v as Date | null);
    const formatted = date ? formatDateToISO(date) : "";
    setValue("sign_date", formatted, { shouldValidate: true });
    setValue("start_date", formatted, { shouldValidate: true });
  }

  const dateValue = signDate ? new Date(signDate) : startDate ? new Date(startDate) : null;

  return (
    <div className="flex flex-col gap-7">
      <h2 className="text-display-2xs content-base-primary">{t("sections.basic_info")}</h2>

      <div className="grid grid-cols-2 gap-y-4 gap-x-3">
        <Input
          label={t("form.contract_number")}
          placeholder={t("placeholders.contract_number")}
          disabled={!contractNumberEditable}
          value={watch("contract_number") ?? ""}
          onChange={(e) => setValue("contract_number", e.target.value.replace(/\D/g, "").slice(0, 18), { shouldValidate: true })}
          onBlur={(e) => setValue("contract_number", e.target.value.replace(/^0+(?=\d)/, ""), { shouldValidate: true })}
          error={errors.contract_number?.message ? t(errors.contract_number.message as string) : contractNumberError}
        />

        <DatePicker
          locale={locale}
          mode="single"
          label={t("form.sign_date")}
          hasRequiredLabel
          placeholder={t("placeholders.sign_date")}
          value={dateValue}
          onChange={handleDateChange}
          error={errors.sign_date?.message ? t(errors.sign_date.message as string) : undefined}
        />

        <RegionCitySelect
          regionLabel={t("form.work_region")}
          hasRequiredLabel
          regionPlaceholder={t("placeholders.work_region")}
          cityLabel={t("form.work_city")}
          cityPlaceholder={t("placeholders.work_city")}
          regionId={regionId}
          onRegionChange={(id, cityType) => {
            setRegionId(id);
            if (cityType === "national_city") {
              setValue("work_city_id", id ?? null, { shouldValidate: true });
            } else {
              setValue("work_city_id", null, { shouldValidate: true });
            }
          }}
          value={workCityId ?? undefined}
          onChange={(cityId) => setValue("work_city_id", cityId ?? null, { shouldValidate: true })}
          cityError={errors.work_city_id?.message ? t(errors.work_city_id.message as string) : undefined}
        />

        <Input
          label={t("form.employee_address_ru")}
          hasRequiredLabel
          placeholder={t("placeholders.employee_address_ru")}
          value={watch("employee_address_ru")}
          onChange={(e) => setValue("employee_address_ru", e.target.value, { shouldValidate: true })}
          error={errors.employee_address_ru?.message ? t(errors.employee_address_ru.message as string) : undefined}
        />

        <Input
          label={t("form.employee_address_kk")}
          hasRequiredLabel
          placeholder={t("placeholders.employee_address_kk")}
          value={watch("employee_address_kk")}
          onChange={(e) => setValue("employee_address_kk", e.target.value, { shouldValidate: true })}
          error={errors.employee_address_kk?.message ? t(errors.employee_address_kk.message as string) : undefined}
        />
      </div>
    </div>
  );
}
